import { useState, useRef } from "react";
import { api, photoUrl, errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { RoleAvatar } from "../RoleAvatar";
import { CropDialog } from "../CropDialog";
import { CityInput } from "../CityInput";
import { formatLocation } from "../../lib/cities";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { toast } from "sonner";
import { Loader2, AtSign, Camera, BadgeCheck } from "lucide-react";

export function ProfileEditor() {
  const { user, updateProfile } = useAuth();
  const { t } = useLanguage();
  const [form, setForm] = useState({
    name: user?.name || "", username: user?.username || "", bio: user?.bio || "", phone: user?.phone || "",
    picture: user?.picture || "", tagline: user?.tagline || "", website: user?.website || "", location: user?.location || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const isStaff = user?.role === "admin" || user?.role === "moderator";

  const onPickPhoto = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please choose an image file");
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB");
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
    if (fileRef.current) fileRef.current.value = "";
  };

  const uploadCropped = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const next = { ...form, picture: data.path };
      setForm(next);
      await updateProfile(next);
      setCropSrc(null);
      toast.success(t("d_photo_updated"));
    } catch (err) { toast.error(errText(err)); }
    finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await updateProfile(form);
      toast.success(t("d_profile_updated"));
    } catch (err) { toast.error(errText(err)); }
    finally { setSaving(false); }
  };

  return (
    <form onSubmit={save} className="max-w-xl bg-white rounded-2xl border border-slate-200/80 p-6 space-y-4" data-testid="profile-form">
      <div className="flex items-center gap-4">
        {isStaff && <RoleAvatar user={user} size={64} />}
        {(
          <div className="relative group">
            <Avatar className="h-16 w-16 border-2 border-slate-200"><AvatarImage src={photoUrl(form.picture)} /><AvatarFallback className="bg-[#12234A] text-white text-xl">{user?.name?.[0]}</AvatarFallback></Avatar>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              data-testid="upload-photo-btn"
              className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-[#F5551E] hover:bg-[#E04812] text-white flex items-center justify-center border-2 border-white shadow"
              title="Upload photo"
            >
              {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
            </button>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickPhoto} data-testid="profile-photo-input" className="hidden" />
          </div>
        )}
        <div>
          <p className="font-display font-bold text-[#12234A]">{t("d_edit_profile")}</p>
          <p className="text-sm text-slate-400">{user?.email}</p>
          {isStaff && <p className="text-xs text-slate-400 mt-0.5 capitalize">{user.role} {t("d_badge_shown")}</p>}
          <p className="text-xs text-slate-400 mt-0.5">{t("d_tap_camera")}</p>
        </div>
      </div>
      <div>
        <Label>{t("d_full_name")}</Label>
        <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="profile-name" className="mt-1 h-11 rounded-xl" required />
      </div>
      <div>
        <Label>{t("d_public_username")}</Label>
        <div className="relative mt-1">
          <AtSign size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={form.username} onChange={(e) => set("username", e.target.value)} data-testid="profile-username" className="pl-9 h-11 rounded-xl" placeholder="your_handle" />
        </div>
        <p className="text-xs text-slate-400 mt-1">{t("d_username_help")}</p>
      </div>
      <div>
        <Label>{t("d_phone_label")}</Label>
        <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="profile-phone" className="mt-1 h-11 rounded-xl" placeholder="(416) 555-0000" />
      </div>
      <div>
        <Label>{t("cityp_label")}</Label>
        <div className="mt-1"><CityInput value={form.location} onChange={(v) => set("location", v)} onPick={(c) => set("location", formatLocation(c.city, c.province))} testid="profile-location" className="h-11 rounded-xl" placeholder="Toronto, ON" /></div>
        <p className="text-xs text-slate-400 mt-1">{t("cityp_hint")}</p>
      </div>
      <div>
        <Label>{t("d_bio")}</Label>
        <Textarea value={form.bio} onChange={(e) => set("bio", e.target.value)} data-testid="profile-bio" className="mt-1 rounded-xl" rows={3} placeholder={t("d_bio_ph")} />
      </div>
      {user?.verified_business && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-3" data-testid="business-card-fields">
          <p className="text-sm font-bold text-amber-700 flex items-center gap-1.5"><BadgeCheck size={15} /> Your Business Directory card</p>
          <div>
            <Label>Tagline <span className="text-slate-400 font-normal">(shown on your directory card)</span></Label>
            <Input value={form.tagline} onChange={(e) => set("tagline", e.target.value)} maxLength={100} data-testid="profile-tagline" className="mt-1 h-11 rounded-xl" placeholder="e.g. CKC-registered golden retriever breeder in the GTA" />
            <p className="text-[11px] text-slate-400 mt-1 text-right">{(form.tagline || "").length}/100</p>
          </div>
          <div>
            <Label>Website</Label>
            <Input value={form.website} onChange={(e) => set("website", e.target.value)} maxLength={200} data-testid="profile-website" className="mt-1 h-11 rounded-xl" placeholder="https://your-business.ca" />
          </div>
        </div>
      )}
      <Button type="submit" disabled={saving} data-testid="save-profile-btn" className="rounded-xl bg-[#F5551E] hover:bg-[#E04812] text-white font-semibold gap-2">
        {saving ? <Loader2 className="animate-spin" size={18} /> : null} {t("d_save_changes")}
      </Button>
      <CropDialog src={cropSrc} open={!!cropSrc} onClose={() => setCropSrc(null)} onCropped={uploadCropped} />
    </form>
  );
}
